import { useEditor, EditorContent } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import {
    FaBold, FaItalic, FaHeading, FaListUl, FaListOl,
    FaQuoteRight, FaImage, FaLink, FaRotateLeft, FaRotateRight,
    FaStrikethrough, FaCode
} from 'react-icons/fa6'
import { useCallback, useEffect } from 'react'
import { getTiptapExtensions, EDITOR_PROPS, SHARED_EDITOR_OPTIONS } from './RichTextEditor.constants'

interface RichTextEditorProps {
    content: string
    onChange: (content: string) => void
    placeholder?: string
}

const MenuBar = ({ editor }: { editor: any }) => {
    if (!editor) return null

    const addImage = useCallback(() => {
        const url = window.prompt('URL de la imagen')
        if (url) {
            editor.chain().focus().setImage({ src: url }).run()
        }
    }, [editor])

    const setLink = useCallback(() => {
        const previousUrl = editor.getAttributes('link').href
        const url = window.prompt('URL del enlace', previousUrl)

        if (url === null) return
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
            return
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }, [editor])

    return (
        <div className="flex flex-wrap gap-2 p-2 border-b border-base-300 bg-base-200/50 rounded-t-box sticky top-0 z-10 backdrop-blur-sm">
            <div className="join">
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={`btn btn-sm join-item ${editor.isActive('bold') ? 'btn-primary' : 'btn-ghost'}`}
                    title="Negrita"
                >
                    <FaBold />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`btn btn-sm join-item ${editor.isActive('italic') ? 'btn-primary' : 'btn-ghost'}`}
                    title="Cursiva"
                >
                    <FaItalic />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    className={`btn btn-sm join-item ${editor.isActive('strike') ? 'btn-primary' : 'btn-ghost'}`}
                    title="Tachado"
                >
                    <FaStrikethrough />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    className={`btn btn-sm join-item ${editor.isActive('code') ? 'btn-primary' : 'btn-ghost'}`}
                    title="Código"
                >
                    <FaCode />
                </button>
            </div>

            <div className="join">
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    className={`btn btn-sm join-item ${editor.isActive('heading', { level: 1 }) ? 'btn-primary' : 'btn-ghost'}`}
                    title="Título 1"
                >
                    <FaHeading className="text-sm" />1
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={`btn btn-sm join-item ${editor.isActive('heading', { level: 2 }) ? 'btn-primary' : 'btn-ghost'}`}
                    title="Título 2"
                >
                    <FaHeading className="text-xs" />2
                </button>
            </div>

            <div className="join">
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={`btn btn-sm join-item ${editor.isActive('bulletList') ? 'btn-primary' : 'btn-ghost'}`}
                    title="Lista con viñetas"
                >
                    <FaListUl />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={`btn btn-sm join-item ${editor.isActive('orderedList') ? 'btn-primary' : 'btn-ghost'}`}
                    title="Lista numerada"
                >
                    <FaListOl />
                </button>
            </div>

            <div className="join">
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    className={`btn btn-sm join-item ${editor.isActive('blockquote') ? 'btn-primary' : 'btn-ghost'}`}
                    title="Cita"
                >
                    <FaQuoteRight />
                </button>
                <button
                    type="button"
                    onClick={setLink}
                    className={`btn btn-sm join-item ${editor.isActive('link') ? 'btn-primary' : 'btn-ghost'}`}
                    title="Enlace"
                >
                    <FaLink />
                </button>
                <button
                    type="button"
                    onClick={addImage}
                    className="btn btn-sm join-item btn-ghost"
                    title="Imagen"
                >
                    <FaImage />
                </button>
            </div>

            <div className="join ml-auto">
                <button
                    type="button"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    className="btn btn-sm join-item btn-ghost"
                    title="Deshacer"
                >
                    <FaRotateLeft />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    className="btn btn-sm join-item btn-ghost"
                    title="Rehacer"
                >
                    <FaRotateRight />
                </button>
            </div>
        </div>
    )
}

export const RichTextEditor = ({ content, onChange, placeholder = 'Empieza a escribir...' }: RichTextEditorProps) => {
    const editor = useEditor({
        ...SHARED_EDITOR_OPTIONS,
        extensions: getTiptapExtensions(placeholder),
        content: content,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML())
        },
        editorProps: EDITOR_PROPS,
    })

    // Update content when prop changes
    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content)
        }
    }, [content, editor])

    return (
        <div className="w-full border border-base-300 rounded-box bg-base-100 overflow-hidden shadow-sm focus-within:border-primary/50 transition-colors">
            <MenuBar editor={editor} />
            {editor && (
                <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
                    <div className="join bg-base-100 shadow-xl border border-base-300 p-1">
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().toggleBold().run()}
                            className={`btn btn-xs join-item ${editor.isActive('bold') ? 'btn-primary' : 'btn-ghost'}`}
                        >
                            <FaBold />
                        </button>
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                            className={`btn btn-xs join-item ${editor.isActive('italic') ? 'btn-primary' : 'btn-ghost'}`}
                        >
                            <FaItalic />
                        </button>
                        <button
                            type="button"
                            onClick={() => editor.chain().focus().toggleLink().run()}
                            className={`btn btn-xs join-item ${editor.isActive('link') ? 'btn-primary' : 'btn-ghost'}`}
                        >
                            <FaLink />
                        </button>
                    </div>
                </BubbleMenu>
            )}
            <EditorContent editor={editor} />
            <style>{`
                .tiptap p.is-editor-empty:first-child::before {
                    color: oklch(var(--bc) / 0.4);
                    content: attr(data-placeholder);
                    float: left;
                    height: 0;
                    pointer-events: none;
                }
            `}</style>
        </div>
    )
}
